import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../AppContext';

const AuthComplete: React.FC = () => {
  const navigate = useNavigate();
  const { completeMagicLinkSignIn } = useApp();
  const [status, setStatus] = useState<'loading' | 'error' | 'success' | 'needs-email'>('loading');
  const [error, setError] = useState('');
  const [inputEmail, setInputEmail] = useState('');

  useEffect(() => {
    const handleMagicLink = async () => {
      try {
        // Get email from localStorage
        let email = window.localStorage.getItem('emailForSignIn');

        if (!email) {
          // User opened link on different device - show email input
          setStatus('needs-email');
          return;
        }

        // Complete the sign-in
        await completeMagicLinkSignIn(email);
        setStatus('success');

        // Get which tool they were accessing (stored when they requested magic link)
        const tool = window.localStorage.getItem('selectedTool') || 'mmt';

        // Redirect to appropriate tool after brief success message
        setTimeout(() => {
          if (tool === 'side-hustle') {
            navigate('/side-hustle/home');
          } else {
            navigate('/mmt/landing');
          }
        }, 1500);
      } catch (err: any) {
        console.error('Magic link error:', err);
        setError(err.message || 'Failed to complete sign in. The link may be invalid or expired.');
        setStatus('error');
      }
    };

    handleMagicLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const handleEmailSubmit = async () => {
    if (!inputEmail) return;

    setStatus('loading');
    try {
      await completeMagicLinkSignIn(inputEmail);
      setStatus('success');

      // Get which tool they were accessing
      const tool = window.localStorage.getItem('selectedTool') || 'mmt';

      setTimeout(() => {
        if (tool === 'side-hustle') {
          navigate('/side-hustle/home');
        } else {
          navigate('/mmt/landing');
        }
      }, 1500);
    } catch (err: any) {
      console.error('Magic link error:', err);
      setError(err.message || 'Failed to complete sign in. The link may be invalid or expired.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex flex-col items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-xl text-center">
        {status === 'needs-email' && (
          <>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Confirm Your Email</h1>
            <p className="text-gray-600 mb-6">
              Please provide your email for confirmation
            </p>
            <input
              type="email"
              value={inputEmail}
              onChange={(e) => setInputEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary mb-4"
              onKeyPress={(e) => e.key === 'Enter' && handleEmailSubmit()}
            />
            <button
              onClick={handleEmailSubmit}
              disabled={!inputEmail}
              className={`w-full py-3 rounded-full font-semibold transition-all ${
                inputEmail
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continue
            </button>
          </>
        )}

        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Completing Sign In...</h1>
            <p className="text-gray-600">Please wait while we verify your email link.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="text-6xl mb-4">✓</div>
            <h1 className="text-2xl font-bold text-green-600 mb-2">Success!</h1>
            <p className="text-gray-600">You're now signed in. Redirecting...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-red-600 mb-4">Sign In Failed</h1>
            <p className="text-gray-700 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-primary/90 transition-all"
            >
              Back to Sign In
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthComplete;
